/**
 * ProvisioningProgress - Animated step-by-step progress display for onboarding.
 *
 * Shows a list of steps with visual states:
 * - pending: gray, waiting
 * - current: animating spinner, highlighted
 * - completed: green checkmark
 * - failed: red alert icon with error message
 *
 * The component auto-advances through steps with simulated timing for a smooth UX.
 */

import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { Check, AlertCircle, Loader2 } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import {
  ONBOARDING_STEPS,
  getStepIndex,
  type OnboardingStep,
  type OnboardingStepInfo,
} from '@/lib/onboarding'
import { cn } from '@/lib/utils'

type StepState = 'pending' | 'current' | 'completed' | 'failed'

interface ProvisioningProgressProps {
  /** Whether provisioning is in progress (animates steps) */
  isProvisioning: boolean
  /** The step that failed (if any) */
  failedStep?: OnboardingStep | null
  /** Error message to display at the failed step */
  errorMessage?: string | null
  /** Callback when all steps complete (if not failed) */
  onComplete?: () => void
  /** Optional action buttons to render below the progress */
  actions?: ReactNode
}

/**
 * Get the visual state for a step based on current progress.
 */
function getStepState(
  stepIndex: number,
  currentStepIndex: number,
  failedStepIndex: number | null
): StepState {
  if (failedStepIndex !== null && stepIndex === failedStepIndex) {
    return 'failed'
  }
  if (failedStepIndex !== null && stepIndex > failedStepIndex) {
    return 'pending'
  }
  if (stepIndex < currentStepIndex) {
    return 'completed'
  }
  if (stepIndex === currentStepIndex) {
    return 'current'
  }
  return 'pending'
}

export function ProvisioningProgress({
  isProvisioning,
  failedStep,
  errorMessage,
  onComplete,
  actions,
}: ProvisioningProgressProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasCompleted = useRef(false)

  // Calculate failed step index if provided
  const failedStepIndex = failedStep ? getStepIndex(failedStep) : null

  // Auto-advance through steps when provisioning
  const advanceStep = useCallback(() => {
    setCurrentStepIndex((prev) => {
      const nextIndex = prev + 1

      // Stop if we've reached a failed step
      if (failedStepIndex !== null && nextIndex > failedStepIndex) {
        return prev
      }

      // Stop if we've completed all steps
      if (nextIndex >= ONBOARDING_STEPS.length) {
        if (!hasCompleted.current) {
          hasCompleted.current = true
          onComplete?.()
        }
        return prev
      }

      return nextIndex
    })
  }, [failedStepIndex, onComplete])

  // Run animation when provisioning starts
  useEffect(() => {
    if (!isProvisioning) {
      // Reset when not provisioning
      setCurrentStepIndex(0)
      hasCompleted.current = false
      return
    }

    // Schedule advancement through steps
    const scheduleNextStep = (stepIndex: number) => {
      if (stepIndex >= ONBOARDING_STEPS.length) return
      if (failedStepIndex !== null && stepIndex > failedStepIndex) return

      const step = ONBOARDING_STEPS[stepIndex]
      timerRef.current = setTimeout(() => {
        advanceStep()
        scheduleNextStep(stepIndex + 1)
      }, step.simulatedDurationMs)
    }

    // Start from current step
    scheduleNextStep(currentStepIndex)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [isProvisioning, failedStepIndex, advanceStep, currentStepIndex])

  // Calculate overall progress percentage
  const progressPercentage =
    failedStepIndex !== null
      ? ((failedStepIndex + 1) / ONBOARDING_STEPS.length) * 100
      : ((currentStepIndex + 1) / ONBOARDING_STEPS.length) * 100

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-[#00000f] p-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-[#141428] border border-gray-200 dark:border-[#252540] rounded-lg p-8 shadow">
          {/* Header */}
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-2">
            Setting up your workspace
          </h2>
          <p className="text-gray-600 dark:text-zinc-400 text-sm text-center mb-6">
            {failedStep
              ? 'Setup encountered an issue'
              : 'Please wait while we configure everything for you'}
          </p>

          {/* Overall progress bar */}
          <div className="mb-8">
            <Progress
              value={progressPercentage}
              className={cn('h-2', failedStep && 'bg-red-100 dark:bg-red-900/20')}
            />
          </div>

          {/* Step list */}
          <div className="space-y-3">
            {ONBOARDING_STEPS.map((step, index) => {
              const state = getStepState(index, currentStepIndex, failedStepIndex)
              return (
                <StepItem
                  key={step.id}
                  step={step}
                  state={state}
                  errorMessage={state === 'failed' ? errorMessage : undefined}
                />
              )
            })}
          </div>

          {/* Action buttons */}
          {actions && <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">{actions}</div>}
        </div>
      </div>
    </div>
  )
}

/**
 * Individual step item with icon and status.
 */
function StepItem({
  step,
  state,
  errorMessage,
}: {
  step: OnboardingStepInfo
  state: StepState
  errorMessage?: string | null
}) {
  const Icon = step.icon

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 rounded-md transition-colors',
        state === 'current' && 'bg-blue-50 dark:bg-blue-900/20',
        state === 'failed' && 'bg-red-50 dark:bg-red-900/20'
      )}
    >
      {/* Icon area */}
      <div className="flex-shrink-0 mt-0.5">
        {state === 'pending' && (
          <div className="h-5 w-5 rounded-full border-2 border-gray-300 dark:border-zinc-600" />
        )}
        {state === 'current' && (
          <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
        )}
        {state === 'completed' && (
          <div className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
            <Check className="h-3 w-3 text-white" />
          </div>
        )}
        {state === 'failed' && (
          <div className="h-5 w-5 rounded-full bg-red-500 flex items-center justify-center">
            <AlertCircle className="h-3 w-3 text-white" />
          </div>
        )}
      </div>

      {/* Text area */}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm font-medium',
            state === 'pending' && 'text-gray-400 dark:text-zinc-500',
            state === 'current' && 'text-gray-900 dark:text-white',
            state === 'completed' && 'text-gray-500 dark:text-zinc-400',
            state === 'failed' && 'text-red-600 dark:text-red-400'
          )}
        >
          {step.label}
        </p>
        {state === 'current' && (
          <p className="text-xs text-gray-500 dark:text-zinc-400 mt-0.5">{step.description}</p>
        )}
        {state === 'failed' && errorMessage && (
          <p className="text-xs text-red-500 dark:text-red-400/80 mt-1">{errorMessage}</p>
        )}
      </div>

      {/* Step icon (decorative) */}
      <div
        className={cn(
          'flex-shrink-0',
          state === 'pending' && 'text-gray-300 dark:text-zinc-600',
          state === 'current' && 'text-blue-600 dark:text-blue-400',
          state === 'completed' && 'text-gray-400 dark:text-zinc-500',
          state === 'failed' && 'text-red-400 dark:text-red-500'
        )}
      >
        <Icon className="h-4 w-4" />
      </div>
    </div>
  )
}
